import PropTypes from 'prop-types';
import { locationShape, routerShape } from 'react-router';

import Ticket from './ticket';

/**
 * Source of truth for all custom data types used in the webapp.
 */

/**
 * Errors
 */
export const apiErrorPropTypeShape = {
  message: PropTypes.string.isRequired,
  status: PropTypes.number,
};

export const apiErrorPropType = PropTypes.shape(apiErrorPropTypeShape);

/**
 * Forms
 */
export const formFieldShape = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
};

export const formFieldPropType = PropTypes.shape(formFieldShape);

// Checkbox
export const checkboxInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  defaultValues: PropTypes.arrayOf(PropTypes.string.isRequired),
};

export const checkboxInputFormFieldPropType = PropTypes.shape(
  checkboxInputFormFieldShape,
);

// Date
export const dateInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  max: PropTypes.string.isRequired,
  min: PropTypes.string.isRequired,
};

export const dateInputFormFieldPropType = PropTypes.shape(
  dateInputFormFieldShape,
);

// Email
export const emailInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
};

export const emailInputFormFieldPropType = PropTypes.shape(
  emailInputFormFieldShape,
);

// Group select
export const groupSelectInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  groupValues: PropTypes.arrayOf(
    PropTypes.shape({
      // alias name for values
      key: PropTypes.string.isRequired,
      values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    }),
  ).isRequired,
  groupDefaultValues: PropTypes.arrayOf(
    PropTypes.shape({
      // alias name for defaultValues
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
};

export const groupSelectInputFormFieldPropType = PropTypes.shape(
  groupSelectInputFormFieldShape,
);

// Heading
export const headingFormFieldShape = {
  ...formFieldShape,
};

export const headingFormFieldPropType = PropTypes.shape(headingFormFieldShape);

// Radio
export const radioInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  defaultValue: PropTypes.string,
};

export const radioInputFormFieldPropType = PropTypes.shape(
  radioInputFormFieldShape,
);

export const reassuranceBoxFormFieldShape = {
  ...formFieldShape,
  message: PropTypes.string,
};

export const reassuranceBoxFormFieldPropType = PropTypes.shape(
  reassuranceBoxFormFieldShape,
);

// Select
export const selectInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  defaultValue: PropTypes.string,
};

export const selectInputFormFieldPropType = PropTypes.shape(
  selectInputFormFieldShape,
);

// Textarea
export const textAreaInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
};

export const textAreaInputFormFieldPropType = PropTypes.shape(
  textAreaInputFormFieldShape,
);

// Text
export const textInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
};

export const textInputFormFieldPropType = PropTypes.shape(
  textInputFormFieldShape,
);

// Address
export const addressInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  subfields: PropTypes.shape({
    CITY: textInputFormFieldPropType,
    COUNTRY: textInputFormFieldPropType,
    LINE_1: textInputFormFieldPropType,
    LINE_2: textInputFormFieldPropType,
    POSTCODE: textInputFormFieldPropType,
    STATE: textInputFormFieldPropType,
  }),
};

export const addressInputFormFieldPropType = PropTypes.shape(
  addressInputFormFieldShape,
);

// Form
export const formShape = {
  fields: PropTypes.arrayOf(
    PropTypes.oneOfType([formFieldPropType.isRequired, PropTypes.shape()])
      .isRequired,
  ).isRequired,
  ticket: Ticket.ticketForm.isRequired,
};

export const formPropType = PropTypes.shape(formShape);

/**
 * Tables
 */
export const tableHeadersShape = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
);

export const tableRowsShape = PropTypes.arrayOf(
  PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
);

/**
 * Images
 */
const imageSizesPropTypeShape = {
  xxlarge: PropTypes.string,
  xlarge: PropTypes.string,
  medium: PropTypes.string,
  large: PropTypes.string,
  thumbnail: PropTypes.string.isRequired,
  original: PropTypes.string,
  facebook: PropTypes.string,
  twitter: PropTypes.string,
  title: PropTypes.string,
};

const imageSizesPropType = PropTypes.shape(imageSizesPropTypeShape);

/**
 * Organisers
 */
export const organiserAbridgedPropTypeShape = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  description: PropTypes.string,
  website: PropTypes.string,
  excerpt: PropTypes.string,
  images: PropTypes.shape({
    logo: imageSizesPropType,
  }),
};

export const organiserAbridgedPropType = PropTypes.shape(
  organiserAbridgedPropTypeShape,
);

export const organiserFullPropTypeShape = {
  ...organiserAbridgedPropTypeShape,
  permalink: PropTypes.string,
  canonical: PropTypes.string.isRequired,
  social: PropTypes.shape({
    facebook: PropTypes.string,
    twitter: PropTypes.string,
    instagram: PropTypes.string,
  }),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
};

export const organiserFullPropType = PropTypes.shape(
  organiserFullPropTypeShape,
);

/**
 * Tickets
 */
export const ticketPropType = Ticket.singleTicket;

/**
 * Races
 */
export const raceAbridgedPropTypeShape = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  excerpt: PropTypes.string,
  canonical: PropTypes.string,
  startDate: PropTypes.string,
  startDateFormatted: PropTypes.string,
  featured: PropTypes.bool,
  tickets: Ticket.ticketSummary,
  keyDetails: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number.isRequired,
      typeDescriptor: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number.isRequired,
      typeDescriptor: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      link: PropTypes.string,
    }).isRequired,
  ),
  information: PropTypes.shape({
    sport: PropTypes.number,
    sportLabel: PropTypes.string,
    distance: PropTypes.number,
    distanceLabel: PropTypes.string,
    metricLength: PropTypes.number,
  }),
  customContent: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ),
};

export const raceAbridgedPropType = PropTypes.shape(raceAbridgedPropTypeShape);

/**
 * Events
 */
export const eventAbridgedPropTypeShape = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  permalink: PropTypes.string,
  canonical: PropTypes.string,
  description: PropTypes.string,
  excerpt: PropTypes.string,
  startDate: PropTypes.string,
  startDateFormatted: PropTypes.string,
  eventUrl: PropTypes.string,
  races: PropTypes.shape({
    bookingEnabled: PropTypes.bool,
    races: PropTypes.arrayOf(raceAbridgedPropType).isRequired,
    available: PropTypes.bool,
    pricesFrom: PropTypes.number,
    pricesTo: PropTypes.number,
    currencyLabel: PropTypes.string,
    notifications: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.number.isRequired,
        typeDescriptor: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
    ),
  }),
  images: PropTypes.shape({
    hero: imageSizesPropType,
  }),
  location: PropTypes.shape({
    lat: PropTypes.number,
    long: PropTypes.number,
    address: PropTypes.arrayOf(PropTypes.string.isRequired),
  }),
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number.isRequired,
      typeDescriptor: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
  saves: PropTypes.shape({
    total: PropTypes.number.isRequired,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }).isRequired,
    ),
  }),
};

export const eventAbridgedPropType = PropTypes.shape(
  eventAbridgedPropTypeShape,
);

export const eventFullPropTypeShape = {
  ...eventAbridgedPropTypeShape,
  organiser: organiserAbridgedPropType,
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number,
      typeDescriptor: PropTypes.string,
      url: PropTypes.string.isRequired,
    }).isRequired,
  ),
  contact: PropTypes.shape({
    email: PropTypes.string,
    phone: PropTypes.string,
    website: PropTypes.string,
  }),
  social: PropTypes.shape({
    facebook: PropTypes.string,
    twitter: PropTypes.string,
    instagram: PropTypes.string,
  }),
  flagship: PropTypes.bool,
  stub: PropTypes.bool,
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

export const eventFullPropType = PropTypes.shape(eventFullPropTypeShape);

export const eventLocationAddressPropTypeShape = {
  city: PropTypes.string,
  countryLong: PropTypes.string,
  countryShort: PropTypes.string,
  county: PropTypes.string,
  formatted: PropTypes.string.isRequired,
  stateLong: PropTypes.string,
  stateShort: PropTypes.string,
};

export const eventLocationAddressPropType = PropTypes.shape(
  eventLocationAddressPropTypeShape,
);

export const eventLocationPropTypeShape = {
  address: eventLocationAddressPropType.isRequired,
  coordinates: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  meta: PropTypes.shape({
    input: PropTypes.string.isRequired,
    locationType: PropTypes.string.isRequired,
    placeId: PropTypes.string.isRequired,
  }).isRequired,
};

export const eventLocationPropType = PropTypes.shape(
  eventLocationPropTypeShape,
);

/**
 * Reviews
 */
export const reviewAbridgedPropTypeShape = {
  id: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  author: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    pictureUrl: PropTypes.string,
  }),
  ratings: PropTypes.shape({
    overall: PropTypes.number,
    course: PropTypes.number,
    organisation: PropTypes.number,
    atmosphere: PropTypes.number,
  }).isRequired,
  review: PropTypes.string.isRequired,
  race: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  year: PropTypes.number.isRequired,
};

export const reviewAbridgedPropType = PropTypes.shape(
  reviewAbridgedPropTypeShape,
);

/**
 * Google Place API-like object (abridged)
 */
export const googlePlaceShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  geometry: PropTypes.shape({
    location: PropTypes.shape({
      lat: PropTypes.func.isRequired,
      lng: PropTypes.func.isRequired,
    }),
    viewport: PropTypes.shape({
      getNorthEast: PropTypes.func.isRequired,
      getSouthWest: PropTypes.func.isRequired,
    }),
  }).isRequired,
});

/**
 * Users
 */
export const userProfileShape = PropTypes.shape({
  displayName: PropTypes.string.isRequired,
  emailAddress: PropTypes.string,
  picture: PropTypes.string.isRequired,
});

/**
 * GraphQL
 */
export const GQLQueryShape = {
  fetchMore: PropTypes.func,
  loading: PropTypes.bool,
  networkStatus: PropTypes.number,
  refetch: PropTypes.func,
  startPolling: PropTypes.func,
  stopPolling: PropTypes.func,
  subscribeToMore: PropTypes.func,
  updateQuery: PropTypes.func,
  variables: PropTypes.shape(),
};

export const GQLQueryPropType = PropTypes.shape(GQLQueryShape);

/**
 * React router
 */
export const routerParamsPropType = PropTypes.objectOf(PropTypes.string);

export { locationShape as routerLocationPropType };

export { routerShape as routerPropType };

export const routerContextShape = {
  router: routerShape,
  location: PropTypes.object,
  route: PropTypes.object,
  routes: PropTypes.array,
  params: PropTypes.object,
  components: PropTypes.array,
  createElement: PropTypes.func,
};

// Geolocation
export const geoLocationShape = PropTypes.shape({
  city: PropTypes.string,
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locationSearchRadius: PropTypes.number,
});

// TODO(ryan@): Expand this to reflect the actual experiment shape.
export const experimentMapShape = PropTypes.shape();

/**
 * Container for category parameters passed to various components on the CategoryPage
 * TODO(ryan@): Expand this to reflect the actual shape.
 */
export const categoryParamsShape = PropTypes.shape();

/**
 * Container for category data passed to various components on the CategoryPage
 * TODO(ryan@): Expand this to reflect the actual shape.
 */
export const categoryDataShape = PropTypes.shape();

export default {
  Ticket,
};

/**
 * Credit codes
 */
export const creditCodesShape = {
  id: PropTypes.string.isRequired,
  maxValue: PropTypes.number,
  percentage: PropTypes.number,
};

export const creditCodesPropsType = PropTypes.shape(creditCodesShape);
